// @TODO: this doesn't look good on small android devices
/**
 * Ryan O'Dowd
 * 2022-03-21
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import IconButton from '@components/common/IconButton';
import {
  KeyboardAvoidingView,
} from 'react-native-keyboard-controller';
import {
  ProgressBar,
} from 'react-native-paper';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import hooks from '@hooks';

const GameTemplate = (props: GameTemplateProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const headerHeight = hooks.useHeaderHeight();

  const [_continueButtonProgress, setContinueButtonProgress] = useState(0);
  const [_isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [_reportNotesText, setReportNotesText] = useState('');
  const [_isReportSent, setIsReportSent] = useState(false);

  const oakUser = hooks.useTypedSelector((state) => state.oakUser);

  useEffect(() => {
    if (props.isIncorrect && _continueButtonProgress < 120) { // @TODO: this 120 is so that the animation lines up better with the actual enabling of the button
      const interval = setInterval(() => {
        setContinueButtonProgress((curr) => {
          if (curr >= 120) {
            clearInterval(interval);
            return curr;
          }
          return curr + 5;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [props.isIncorrect, _continueButtonProgress]);

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView// @TODO: not avoiding in first letters if verse is long enough
        style={styles.innerContainer}
        behavior='padding'
        keyboardVerticalOffset={headerHeight + props.subHeaderHeight}
      >
        <View style={styles.minigameContainer}>
          <View
            style={[
              styles.scrollWrapper,
              props.isTest && styles.scrollWrapperTests,
              props.innerContainerStyles,
              props.isIncorrect && styles.incorrectInnerContainer,
              props.isCorrect && styles.correctInnerContainer,
              props.isCorrect && props.isIncorrect && styles.mixedCorrectnessInnerContainer,
            ]}
          >
            <ScrollView
              contentContainerStyle={[
                styles.minigameInnerContainer,
                props.isTest && !props.questionComplete && styles.minigameInnerContainerTests,
                props.innerContainerStyles,
              ]}
              scrollEnabled={!props.scrollDisabled}
              bounces={false}
              keyboardShouldPersistTaps='handled'// especially helpful for selecting autocomplete option in references game without dismissing keyboard // @TODO: this comment was for when it was 'always'
            >
              {props.children}
            </ScrollView>
          </View>
          <View style={styles.buttonWrapper}>
            {props.questionComplete
              ? (
                <View style={styles.actionsWrapper}>
                  {props.isTest && props.isIncorrect && !oakUser.is_feedback_chat_disabled &&
                    <IconButton
                      style={styles.reportIcon}
                      icon='flag'
                      iconColor={(props.isCorrect && props.isIncorrect) ? styles.colors.complete : styles.colors.incorrect}
                      size={22 * styles.globalScale}
                      onPress={() => setIsReportDialogOpen(true)}
                    />
                  }
                  <View style={styles.continueButtonWrapper}>
                    <Button
                      containerStyle={[
                        styles.mainButton,
                        props.isIncorrect && styles.incorrectButton,
                        props.isCorrect && styles.correctButton,
                        props.isCorrect && props.isIncorrect && styles.mixedCorrectnessButton,
                      ]}
                      style={[
                        styles.mainButton,
                        props.isIncorrect && styles.incorrectButton,
                        props.isCorrect && styles.correctButton,
                        props.isCorrect && props.isIncorrect && styles.mixedCorrectnessButton,
                      ]}
                      isLarge={true}
                      mode='contained'
                      theme={(props.isCorrect && props.isIncorrect) ? styles.colors.complete : (props.isCorrect ? styles.colors.accent : styles.colors.incorrect)}
                      disabled={props.isIncorrect && _continueButtonProgress < 120}
                      onPress={() => props.onPass()}
                    >
                      {i18n('continue')}
                    </Button>
                    {!!(props.isIncorrect && _continueButtonProgress < 120) &&
                      <View style={styles.progressBarWrapper}>
                        <ProgressBar
                          style={styles.disabledProgressBar}
                          progress={_continueButtonProgress / 100}
                          color={styles.colors.incorrect}
                        />
                      </View>
                    }
                  </View>
                </View>
              ) : (
                <Button
                  containerStyle={styles.mainButton}
                  style={styles.mainButton}
                  isLarge={true}
                  mode='contained'
                  theme={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary}
                  disabled={props.isCheckAnswerButtonDisabled}
                  onPress={() => props.onCheckAnswer()}
                >{/* @TODO: better text */}
                  {i18n('check')}
                </Button>
              )
            }
          </View>
        </View>
      </KeyboardAvoidingView>
      {_isReportDialogOpen &&
        <Dialog// @TODO: this should be in a KeyboardAvoidingView on mobile? or at least the textinput
          visible={_isReportDialogOpen}
          onDismiss={() => setIsReportDialogOpen(false)}
        >
          <Dialog.Title>{i18n('reportTest')}</Dialog.Title>
          {/* @TODO: need a keyboardavoidingview here */}
          <Dialog.Content>
            {_isReportSent
              ? (
                <Text style={styles.dialogText}>{i18n('reportTestThanks')}</Text>
              ) : (
                <>
                  <Text style={styles.dialogText}>{i18n('reportTestInstructions')}</Text>
                  <TextInput
                    mode='outlined'
                    textColor={styles.colors.dialogText}
                    selectionColor={styles.colors.primaryDark}
                    defaultValue={_reportNotesText}// @TODO: https://github.com/jeremybarbet/react-native-portalize/issues/6#issuecomment-1020757814
                    multiline={true}
                    numberOfLines={3}
                    onChangeText={(t) => setReportNotesText(t)}
                    placeholder={i18n('notesOptional')}
                  />
                </>
              )
            }
          </Dialog.Content>
          <Dialog.Actions>
            {_isReportSent
              ? (
                <Button
                  mode='contained'
                  onPress={() => {
                    setIsReportDialogOpen(false);
                  }}
                >
                  {i18n('close')}
                </Button>
              ) : (
                <>
                  <Button
                    mode='text'
                    onPress={() => {
                      setIsReportDialogOpen(false);
                    }}
                  >
                    {i18n('cancel')}
                  </Button>
                  <Button
                    mode='contained'
                    onPress={() => {
                      props.onReport(_reportNotesText);
                      setIsReportSent(true);
                    }}
                  >
                    {i18n('report')}
                  </Button>
                </>
              )
            }
          </Dialog.Actions>
        </Dialog>
      }
    </View>
  );
};

class GameTemplateProps {
  children: React.ReactNode;
  isCorrect: boolean;
  isIncorrect: boolean;
  isCheckAnswerButtonDisabled: boolean;
  onCheckAnswer: () => void;
  onPass: () => void;
  questionComplete: boolean;
  subHeaderHeight: number;

  innerContainerStyles?: object; // @TODO: more specific
  isTest?: boolean;
  onReport?: (t) => void; // @TODO: required if Test type
  scrollDisabled?: boolean; // order the verses handles its own scroll
};

export default GameTemplate;
